import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { ChevronRight, LogOut, Menu as MenuGlyph } from './icons';
import { Icon } from './Icon';
import { IconButton } from './IconButton';
import { NARROW_WIDTH } from './layoutStyles';
import { focusRing } from './createWorkspaceTheme';
import { NavDrawer } from './NavDrawer';
import type {
  WsShellAppGroup,
  WsShellBreadcrumb,
  WsShellModule,
  WsShellRecent,
} from './shellTypes';
import { useContainerWidth } from './useContainerWidth';
import { WS_COLOR, WS_CONTROL, WS_FRAME, WS_STATUS } from './tokens';

export type {
  WsShellApp,
  WsShellAppGroup,
  WsShellSection,
  WsShellModule,
  WsShellBreadcrumb,
  WsShellRecent,
} from './shellTypes';

export interface AppShellProps {
  brand: string;
  /** Root-state applications, grouped: entitled AND assigned only. */
  appGroups: WsShellAppGroup[];
  /** Set to drill the drawer into a module; omit for the root state. */
  activeModule?: WsShellModule;
  activeSectionKey?: string;
  /** Keys of every section on the path to the current screen, outermost first. */
  activeTrailKeys?: readonly string[];
  breadcrumbs: WsShellBreadcrumb[];
  onNavigate: (path: string) => void;
  user?: { displayName: string; email?: string };
  onSignOut?: () => void;
  /**
   * Show the navigation drawer as a picture (a storyboard panel). It does not trap focus
   * or take the page's keyboard while it is shown this way; the first time it is closed, it
   * behaves as normal.
   */
  navPreview?: boolean;
  /** Ids of starred applications. Omit `onToggleFavorite` to hide favourites. */
  favoriteIds?: readonly string[];
  onToggleFavorite?: (id: string) => void;
  /**
   * A slim row under the screen, always visible (the vendor attribution and version). The
   * screens fill the area above it; they never scroll under it.
   */
  footer?: ReactNode;
  /** Peek at an application's sections on hover. Default on. */
  navPeek?: boolean;
  /**
   * The screen area scrolls when its content is taller than the window. For screens that still
   * scroll the page (ported originals shown inside this shell); workspace screens fill the
   * area and never trigger it.
   */
  contentScroll?: boolean;
  /** Screens visited lately, newest first. */
  recents?: readonly WsShellRecent[];
  /** Right side of the platform bar, left of the user menu. */
  toolbarActions?: ReactNode;
  versionLabel?: string;
  /**
   * `viewport`: the shell is the window (100vh, never scrolls). `parent`: it fills a parent of
   * definite height (storyboard frames, embedded previews).
   */
  fill?: 'viewport' | 'parent';
  /**
   * Draw real landmarks (`header`, `nav`, `main`). Turn off only when the shell is a picture of a
   * page inside another page, where a second `main` would be wrong.
   */
  landmarks?: boolean;
  children: ReactNode;
}

const PIN_KEY = 'ws.shell.navPinned';

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');

/**
 * The frame every workspace screen renders in (DESIGN.md "Layout": 56px platform bar, 24px
 * gutters, the whole width for the screen). It carries the two coupled behaviours of
 * docs/architecture/navigation-and-breadcrumbs.md:
 *
 *  1. DRILL-IN NAVIGATION DRAWER (NavDrawer, opened from the menu button or `.`): at root it lists
 *     every application the person can open; enter one and the others go, replaced by a back row,
 *     the module header and that module's sections (buckets collapse).
 *  2. BREADCRUMB TRAIL in the bar, every level a link, mirroring the URL.
 *
 * Both are driven by props from the route, never internal state, so any URL renders cold.
 *
 * The shell is a size container: at 899px of its own width or less the breadcrumb trail shortens,
 * so a narrow frame behaves like a phone whatever the window is. The page never scrolls: `<main>`
 * fills the space and its screen's layout owns the scroll regions.
 */
export function AppShell({
  brand,
  appGroups,
  activeModule,
  activeSectionKey,
  activeTrailKeys = [],
  breadcrumbs,
  onNavigate,
  user,
  onSignOut,
  navPreview = false,
  navPeek = true,
  footer,
  contentScroll = false,
  favoriteIds,
  onToggleFavorite,
  recents,
  toolbarActions,
  versionLabel,
  fill = 'viewport',
  landmarks = true,
  children,
}: AppShellProps) {
  const [widthRef, width] = useContainerWidth<HTMLDivElement>();
  const narrow = width !== undefined && width <= NARROW_WIDTH;
  const [navOpen, setNavOpen] = useState(navPreview);
  /*
   * PINNED NAVIGATION (owner request 2026-10-23: "the ability to pin the left section of the
   * screen where the menu is presented"). The same drawer, docked as a column beside the screen.
   * Remembered per browser; a narrow frame never docks, it keeps the overlay.
   */
  const [pinned, setPinnedState] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(PIN_KEY) === '1';
    } catch {
      return false;
    }
  });
  const setPinned = (v: boolean) => {
    setPinnedState(v);
    try {
      window.localStorage.setItem(PIN_KEY, v ? '1' : '0');
    } catch {
      /* private window: the pin holds for this visit only */
    }
  };
  const docked = pinned && !narrow && !navPreview;
  const [previewing, setPreviewing] = useState(navPreview);
  const [userAnchor, setUserAnchor] = useState<HTMLElement | null>(null);
  // The element a contained drawer (`fill="parent"`) stays inside.
  const [shellEl, setShellEl] = useState<HTMLDivElement | null>(null);
  const shellRef = useCallback(
    (node: HTMLDivElement | null) => {
      widthRef(node);
      setShellEl(node);
    },
    [widthRef],
  );

  const go = (path: string) => {
    onNavigate(path);
    setNavOpen(false);
    setPreviewing(false);
  };

  // `.` opens the navigation, as in the Google Cloud console, unless a field is taking text.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '.' || e.ctrlKey || e.metaKey || e.altKey || e.defaultPrevented) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      e.preventDefault();
      setNavOpen(true);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const Header = landmarks ? 'header' : 'div';
  const Main = landmarks ? 'main' : 'div';

  const shownCrumbs = narrow ? breadcrumbs.slice(-2) : breadcrumbs;

  return (
    <Box
      ref={shellRef}
      sx={{
        position: 'relative',
        height: fill === 'viewport' ? '100vh' : '100%',
        width: '100%',
        display: 'grid',
        gridTemplateColumns: docked
          ? `${WS_FRAME.drawerWidth}px minmax(0, 1fr)`
          : 'minmax(0, 1fr)',
        gridTemplateRows: footer
          ? `${WS_FRAME.barHeight}px minmax(0, 1fr) ${WS_FRAME.footerHeight}px`
          : `${WS_FRAME.barHeight}px minmax(0, 1fr)`,
        containerType: 'inline-size',
        backgroundColor: WS_COLOR.background,
        overflow: 'hidden',
      }}
    >
      <Box
        component={Header}
        sx={{
          gridColumn: '1 / -1',
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          px: narrow ? 2 : 5,
          backgroundColor: WS_COLOR.surface,
          borderBottom: `1px solid ${WS_COLOR.border}`,
          minWidth: 0,
          '& :focus-visible': focusRing,
        }}
      >
        <Tooltip title="Navigation menu (.)" describeChild>
          <IconButton
            icon={MenuGlyph}
            label="Navigation menu"
            aria-haspopup="dialog"
            aria-keyshortcuts="."
            aria-expanded={navOpen || docked}
            onClick={() => (docked ? setPinned(false) : setNavOpen(true))}
          />
        </Tooltip>
        <Box
          aria-hidden
          sx={{
            width: 28,
            height: 28,
            flex: 'none',
            borderRadius: '8px',
            backgroundColor: WS_COLOR.primary,
            color: WS_COLOR.onPrimary,
            display: 'grid',
            placeItems: 'center',
            fontWeight: 700,
          }}
        >
          {brand.slice(0, 1)}
        </Box>
        <Typography sx={{ fontWeight: 600, flex: 'none' }}>{brand}</Typography>

        <Box component="nav" aria-label="Breadcrumb" sx={{ flex: 1, minWidth: 0, ml: 3 }}>
          <Box
            component="ol"
            sx={{
              listStyle: 'none',
              m: 0,
              p: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              fontSize: 14,
              minWidth: 0,
            }}
          >
            {shownCrumbs.map((c, i) => {
              const last = i === shownCrumbs.length - 1;
              return (
                <Box
                  component="li"
                  key={`${c.label}-${i}`}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}
                >
                  {i > 0 ? <Icon icon={ChevronRight} size={16} /> : null}
                  {c.path && !last ? (
                    <Box
                      component="button"
                      type="button"
                      onClick={() => go(c.path!)}
                      sx={{
                        border: 0,
                        background: 'none',
                        font: 'inherit',
                        p: 0,
                        color: WS_COLOR.primary,
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {c.label}
                    </Box>
                  ) : (
                    <Box
                      component="span"
                      aria-current={last ? 'page' : undefined}
                      sx={{
                        fontWeight: last ? 600 : 400,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {c.label}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>

        {toolbarActions}
        {user ? (
          <>
            <Box
              component="button"
              type="button"
              aria-label={`Account: ${user.displayName}`}
              aria-haspopup="menu"
              aria-expanded={Boolean(userAnchor)}
              onClick={(e) => setUserAnchor(e.currentTarget)}
              sx={{
                width: WS_CONTROL.hitArea,
                height: WS_CONTROL.hitArea,
                flex: 'none',
                border: 0,
                borderRadius: '50%',
                background: 'none',
                cursor: 'pointer',
                display: 'grid',
                placeItems: 'center',
                p: 0,
              }}
            >
              <Box
                aria-hidden
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  backgroundColor: WS_STATUS.info.fill,
                  color: WS_STATUS.info.ink,
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {initialsOf(user.displayName)}
              </Box>
            </Box>
            <Menu
              anchorEl={userAnchor}
              open={Boolean(userAnchor)}
              onClose={() => setUserAnchor(null)}
            >
              <Box sx={{ px: 3, py: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {user.displayName}
                </Typography>
                {user.email ? (
                  <Typography variant="caption" sx={{ color: WS_COLOR.textMuted }}>
                    {user.email}
                  </Typography>
                ) : null}
              </Box>
              {onSignOut ? (
                <MenuItem
                  onClick={() => {
                    setUserAnchor(null);
                    onSignOut();
                  }}
                >
                  <Box sx={{ display: 'flex', mr: 2 }}>
                    <Icon icon={LogOut} size={16} />
                  </Box>
                  Sign out
                </MenuItem>
              ) : null}
            </Menu>
          </>
        ) : null}
      </Box>

      {
        // A contained drawer waits for its container: rendered before it exists it would open
        // over the whole window instead.
        fill === 'parent' && !shellEl ? null : (
          <NavDrawer
            open={navOpen}
            onClose={() => {
              setNavOpen(false);
              setPreviewing(false);
            }}
            trapFocus={!previewing}
            brand={brand}
            appGroups={appGroups}
            {...(activeModule ? { activeModule } : {})}
            {...(activeSectionKey ? { activeSectionKey } : {})}
            activeTrailKeys={activeTrailKeys}
            onNavigate={onNavigate}
            {...(favoriteIds ? { favoriteIds } : {})}
            {...(onToggleFavorite ? { onToggleFavorite } : {})}
            {...(recents ? { recents } : {})}
            {...(versionLabel ? { versionLabel } : {})}
            peek={navPeek}
            {...(fill === 'parent' ? { container: shellEl } : {})}
            docked={docked}
            {...(narrow || navPreview
              ? {}
              : {
                  onTogglePin: () => {
                    // Pinning docks the open drawer; unpinning closes it back behind ☰.
                    setPinned(!pinned);
                    setNavOpen(false);
                  },
                })}
          />
        )
      }

      <Box
        component={Main}
        data-ws-main=""
        sx={{
          gridRow: 2,
          gridColumn: docked ? 2 : 1,
          minHeight: 0,
          minWidth: 0,
          px: `${WS_FRAME.gutter}px`,
          pt: `${WS_FRAME.gutter - 4}px`,
          pb: `${WS_FRAME.gutter}px`,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            ...(contentScroll ? { overflowY: 'auto' } : {}),
          }}
        >
          {children}
        </Box>
      </Box>

      {footer ? (
        <Box
          component={landmarks ? 'footer' : 'div'}
          sx={{
            gridColumn: '1 / -1',
            height: WS_FRAME.footerHeight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            minWidth: 0,
            backgroundColor: WS_COLOR.surface,
            borderTop: `1px solid ${WS_COLOR.border}`,
          }}
        >
          {footer}
        </Box>
      ) : null}
    </Box>
  );
}
